import React, { useRef } from 'react';
import { EntityHubSection, SectionStatus } from './entityHubSections';

export interface EntityHubStatusTab {
  key: EntityHubSection;
  label: string;
  status: SectionStatus;
}

interface EntityHubStatusTabsProps {
  tabs: EntityHubStatusTab[];
  active: EntityHubSection;
  onSelect: (section: EntityHubSection) => void;
}

// Column weights, 1:2:2. Business Verification and Deep Dive carry longer
// status lines (a check count, a running check's name and clock) than the
// screening verdict, so they get the room.
const SECTION_WEIGHT: Record<EntityHubSection, number> = {
  screening: 1,
  verification: 2,
  'deep-dive': 2,
};

/** The tab bar's grid columns, in the tabs' own order and weights. */
export const sectionTabColumns = (tabs: EntityHubStatusTab[]): string =>
  tabs.map((tab) => `minmax(0, ${SECTION_WEIGHT[tab.key]}fr)`).join(' ');

export const sectionTabId = (key: EntityHubSection): string =>
  `entity-hub-tab-${key}`;
export const sectionPanelId = (key: EntityHubSection): string =>
  `entity-hub-panel-${key}`;

/**
 * The record's sections as tabs, each saying what is waiting behind it:
 * the screening verdict, the verification result, the Deep Dive checks.
 */
const EntityHubStatusTabs: React.FC<EntityHubStatusTabsProps> = ({
  tabs,
  active,
  onSelect,
}) => {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Arrow keys move between tabs and select them (the WAI-ARIA tabs pattern).
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const last = tabs.length - 1;
    const next =
      event.key === 'ArrowRight'
        ? index === last
          ? 0
          : index + 1
        : event.key === 'ArrowLeft'
        ? index === 0
          ? last
          : index - 1
        : event.key === 'Home'
        ? 0
        : event.key === 'End'
        ? last
        : null;
    if (next === null) return;
    event.preventDefault();
    tabRefs.current[next]?.focus();
    onSelect(tabs[next].key);
  };

  return (
    <div
      className='entity-hub-status-tabs'
      role='tablist'
      aria-label='Record sections'
      style={{ gridTemplateColumns: sectionTabColumns(tabs) }}
    >
      {tabs.map((tab, index) => {
        const selected = tab.key === active;
        return (
          <button
            key={tab.key}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            type='button'
            role='tab'
            id={sectionTabId(tab.key)}
            aria-selected={selected}
            aria-controls={sectionPanelId(tab.key)}
            tabIndex={selected ? 0 : -1}
            className={`entity-hub-status-tab${selected ? ' is-selected' : ''}`}
            onClick={() => onSelect(tab.key)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            <span className='entity-hub-status-tab-label'>{tab.label}</span>
            <span
              className={`entity-hub-status-tab-status tone-${tab.status.tone}`}
            >
              <span className='entity-hub-status-tab-dot' aria-hidden='true' />
              {tab.status.text}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default EntityHubStatusTabs;
